import {Alert} from '@mui/material'
import React from 'react'
import {generatePath, Link as RouterLink} from 'react-router-dom'
import {Routes} from '../../common/Routes'
import {Box} from '../../webui/Box'
import {Grid} from '../../webui/Grid'
import {Link} from '../../webui/Link'
import {LoadingSpinnerContainer} from '../../webui/progress/LoadingSpinnerContainer'
import {MenuItem} from '../../webui/inputs/MenuItem'
import {Select, SelectInputProps} from '../../webui/inputs/Select'
import {TextField, TextFieldProps} from '../../webui/inputs/TextField'
import {Typography} from '../../webui/Typography'
import {AddLocation} from '../location/add/AddLocation'
import {PriceWeightSelect} from '../price/PriceWeightSelect'
import {SponsorSelect} from '../sponsor/SponsorSelect'
import {AddCompartment} from './AddCompartment'
import {BoxColorSelect} from './BoxColorSelect'
import {SerialNumberPrompt, SerialPromptResult} from './add/SerialNumberPrompt'
import {useAddBoxCompartments} from './add/useAddBoxCompartments'
import {useAddBoxForm} from './add/useAddBoxForm'
import {useAllOrganisations} from '../organisation/gql/useAllOrganisations'
import {useAllBoxesForSelect} from './gql/useAllBoxesForSelect'
import {GQL_ContractType, useBoxesForSerialAdminQuery} from '../../generated/graphql'
import {contractTypeLabel} from '../../common/domain/contractType'
import {SaveButton} from '../../webui/buttons/SaveButton'

interface AddBoxProps {}

export const AddBox: React.FC<AddBoxProps> = () => {
  const {compartmentForms, resetAllCompartments} = useAddBoxCompartments()

  const {data: organisations, loading: organisationsLoading} = useAllOrganisations()

  const {data: boxesForSelect, loading: boxesForSelectLoading} = useAllBoxesForSelect()

  // Source for the serial-prompt's best-effort "is this serial already on another box?" check.
  const {data: serialAdminData, loading: serialAdminLoading} = useBoxesForSerialAdminQuery()

  const hook: any = useAddBoxForm({
    compartmentForms,
    resetAllCompartments,
    organisations,
    boxesForSelect,
  })

  const form = hook.form
  const updateField = hook.updateField
  const locationForm = hook.locationForm

  const loading = hook.loading
  const onSave = hook.onSave

  // The serial-number question is asked up front, when the user lands on Add Box.
  const [serialPromptOpen, setSerialPromptOpen] = React.useState(true)
  // Bumped on "create another" to remount the prompt with fresh state (reopening via "change"
  // keeps the previous entry).
  const [serialPromptInstance, setSerialPromptInstance] = React.useState(0)

  const existingSerials = React.useMemo(
    () =>
      (serialAdminData?.box?.allBoxes ?? [])
        .filter((box): box is {id: string; name: string; serialNumber: string} => Boolean(box.serialNumber))
        .map((box) => ({id: box.id, name: box.name, serialNumber: box.serialNumber as string})),
    [serialAdminData?.box?.allBoxes],
  )

  const onSerialPromptConfirm = (result: SerialPromptResult) => {
    updateField('serialNumber', result.serialNumber)
    updateField('serialTransferFromBoxId', result.transferFromBoxId)
    updateField('serialTransferFromBoxName', result.transferFromBoxName)
    setSerialPromptOpen(false)
  }

  // Cancelling the prompt just proceeds with no serial (it can be assigned later).
  const onSerialPromptCancel = () => {
    updateField('serialNumber', '')
    updateField('serialTransferFromBoxId', '')
    updateField('serialTransferFromBoxName', '')
    setSerialPromptOpen(false)
  }

  // Re-ask the serial question when starting a fresh box via "create another".
  const onCreateAnother = (e?: any) => {
    hook.onCreateAnother(e)
    setSerialPromptInstance((n) => n + 1)
    setSerialPromptOpen(true)
  }

  const createdBoxId = hook.success?.boxId ?? null
  const createdBoxName = hook.success?.boxLabel ?? null
  const serialWarning = hook.success?.serialWarning ?? null
  const serialMove = hook.success?.serialMove ?? null

  const numberOfCompartmentOptions =
    hook.numberOfCompartmentOptions ??
    [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16].map((n) => ({
      label: String(n),
      value: n,
    }))

  if (organisationsLoading || boxesForSelectLoading || serialAdminLoading) {
    return <LoadingSpinnerContainer />
  }

  /* ===========================
   * Success screen
   * =========================== */
  if (createdBoxId) {
    // createdBoxName may already be "Box 1234" depending on hook implementation.
    const label = typeof createdBoxName === 'string' && createdBoxName.trim().length > 0 ? createdBoxName.trim() : createdBoxId

    const linkText = label.startsWith('Box ') ? label : `Box ${label}`

    return (
      <Box display="inline-flex" flexDirection="column">
        <Typography variant="h4">Add box</Typography>

        <Box mt={3}>
          <Typography variant="body1">
            <Link component={RouterLink} to={generatePath(Routes.BOX, {id: createdBoxId})} sx={{fontWeight: 600}}>
              {linkText}
            </Link>{' '}
            was added.
          </Typography>

          {serialMove && (
            <Box mt={2}>
              <Alert severity="info">
                <Typography variant="body2">
                  Serial number <strong>{serialMove.serial}</strong> was moved here from{' '}
                  <Link component={RouterLink} to={generatePath(Routes.BOX, {id: serialMove.fromBoxId})} sx={{fontWeight: 600}}>
                    {serialMove.fromBoxName || 'the previous box'}
                  </Link>
                  , which no longer has a serial number. Review that box and deactivate it if it's no longer needed.
                </Typography>
              </Alert>
            </Box>
          )}

          {serialWarning && (
            <Box mt={2}>
              <Alert severity="warning">{serialWarning}</Alert>
            </Box>
          )}

          <Box mt={2} display="flex" alignItems="baseline">
            <Typography variant="body1" component="span">
              Create&nbsp;
            </Typography>
            <Link
              component="button"
              onClick={onCreateAnother}
              sx={{
                fontWeight: 600,
                textDecoration: 'none',
                padding: 0,
                minWidth: 'unset',
              }}
            >
              another one
            </Link>
            <Typography variant="body1" component="span">
              ?
            </Typography>
          </Box>
        </Box>
      </Box>
    )
  }

  /* ===========================
   * Form handlers
   * =========================== */
  const onNameChange: TextFieldProps['onChange'] = (value) => updateField('name', value)

  const onCircuitBoardIdChange: TextFieldProps['onChange'] = (value) => updateField('circuitBoardId', value)

  const onSponsorChange = (value: string) => updateField('sponsorId', value)

  const onColorChange = (value: any) => updateField('color', value)

  const onPriceWeightChange: SelectInputProps<string>['onChange'] = (e) => updateField('priceWeight', e.target.value as string)

  const onOrganisationChange: SelectInputProps<string>['onChange'] = (e) => updateField('organisationId', e.target.value as string)

  const onContractTypeChange: SelectInputProps<string>['onChange'] = (e) => updateField('contractType', e.target.value as string)

  const onCompartmentChange: SelectInputProps<number>['onChange'] = (event) => updateField('numberOfCompartments', event.target.value as number)

  /* ===========================
   * Form UI
   * =========================== */
  return (
    <Box display="inline-flex" flexDirection="column">
      <SerialNumberPrompt
        key={serialPromptInstance}
        open={serialPromptOpen}
        existingSerials={existingSerials}
        onConfirm={onSerialPromptConfirm}
        onCancel={onSerialPromptCancel}
      />

      <Typography variant="h4">Add box</Typography>

      <Box mt={1} display="flex" alignItems="baseline">
        <Typography variant="body2" component="span">
          Serial number:&nbsp;
          {form.serialNumber ? <strong>{form.serialNumber}</strong> : 'will be assigned later'}
          {form.serialNumber && form.serialTransferFromBoxName ? ` (moving from ${form.serialTransferFromBoxName})` : ''}
        </Typography>
        <Link
          component="button"
          onClick={() => setSerialPromptOpen(true)}
          sx={{ml: 1, fontWeight: 600, textDecoration: 'none', padding: 0, minWidth: 'unset'}}
        >
          change
        </Link>
      </Box>

      <Grid container spacing={1} alignItems="flex-end">
        <Grid>
          <TextField label="Name (4-digit number)" onChange={onNameChange} value={form.name} />
        </Grid>

        <Grid>
          <TextField label="Circuit Board Id" onChange={onCircuitBoardIdChange} value={form.circuitBoardId} />
        </Grid>

        <Grid>
          <Select label="Compartments" value={form.numberOfCompartments} onChange={onCompartmentChange}>
            {numberOfCompartmentOptions.map((option: {label: string; value: number}) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid>
          <BoxColorSelect id="box-color" value={form.color} onChange={onColorChange} />
        </Grid>

        <Grid>
          <PriceWeightSelect id="box-priceWeight" value={form.priceWeight} onChange={onPriceWeightChange} />
        </Grid>

        <Grid>
          <Select label="Organisation" value={form.organisationId ?? ''} onChange={onOrganisationChange}>
            <MenuItem value="">Not set</MenuItem>
            {organisations.map((org: any) => (
              <MenuItem key={org.id} value={org.id}>
                {org.name}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid>
          <SponsorSelect id="box-sponsor" value={form.sponsorId} onChange={onSponsorChange} />
        </Grid>

        <Grid>
          <Select label="Contract type" value={form.contractType ?? ''} onChange={onContractTypeChange}>
            <MenuItem value="">Not set</MenuItem>
            <MenuItem value={GQL_ContractType.Care}>{contractTypeLabel(GQL_ContractType.Care)}</MenuItem>
            <MenuItem value={GQL_ContractType.CarePlus}>{contractTypeLabel(GQL_ContractType.CarePlus)}</MenuItem>
            <MenuItem value={GQL_ContractType.FullService_1}>{contractTypeLabel(GQL_ContractType.FullService_1)}</MenuItem>
            <MenuItem value={GQL_ContractType.FullService_2}>{contractTypeLabel(GQL_ContractType.FullService_2)}</MenuItem>
            <MenuItem value={GQL_ContractType.Firewood}>{contractTypeLabel(GQL_ContractType.Firewood)}</MenuItem>
            <MenuItem value={GQL_ContractType.Minigolf}>{contractTypeLabel(GQL_ContractType.Minigolf)}</MenuItem>
          </Select>
        </Grid>
      </Grid>

      <Box mt={2}>
        <AddLocation form={locationForm.form} updateField={locationForm.updateField} />
      </Box>

      <Box mt={2}>
        <Typography variant="h5">Compartments</Typography>
      </Box>

      <Box mt={2}>
        <Grid container spacing={1}>
          {compartmentForms.slice(0, form.numberOfCompartments).map((compForm: any, index: number) => (
            <Grid key={index}>
              <AddCompartment updateField={compForm.updateField} form={compForm.form} idPrefix={String(index + 1)} />
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box mt={4} display="flex" justifyContent="center">
        <Box
          sx={{
            '& button': {
              minWidth: 600, // ~5× the original SaveButton (~120px)
            },
          }}
        >
          <SaveButton loading={loading} onClick={onSave} />
        </Box>
      </Box>
    </Box>
  )
}
